import {Module} from 'vuex';
import api from '@/services/api';
import {RootState} from '@/types';
import {StudentProfile, IStudentProfile} from '@/classes/StudentProfile';
import _ from 'underscore';
import store from '@/store';
import UserProfile from '@/classes/UserProfile';
import authorize, {Office} from '@/services/Authorize';

export interface StudentState {
    students: StudentProfile[];
}

export const StudentStore: Module<StudentState, RootState> = {
    namespaced: true,

    state: {
        students: []
    },

    getters: {
        getStudents(state): StudentProfile[] {
            const activeProfile: UserProfile | undefined = store.getters.getActiveProfile;
            if  (authorize(Office)) {
                return state.students;
            } else if (activeProfile !== undefined && activeProfile.jobCorpsCenterIds !== undefined) {
                return state.students.filter((s: StudentProfile) => {
                    if (s.jobCorpsCenterId !== undefined) {
                        if (activeProfile !== undefined && activeProfile.jobCorpsCenterIds !== undefined) {
                            return activeProfile.jobCorpsCenterIds.includes(s.jobCorpsCenterId);
                        }
                    } else {
                        return false;
                    }
                });
            } else {
                return [];
            }
        },

        getStudentById: (state) => (sId: string) => {
            const id = Number(sId);

            return _.find(state.students, (s: StudentProfile) => {
                return s.id === id;
            });
        },

        getStudentIdsByLastNameString: (state) => (searchString: string): number[] => {
            return _.chain(state.students)
                .filter((s: StudentProfile) => {
                    return null === searchString || (undefined !== s.lastName && new RegExp(searchString, 'i').test(s.lastName));
                })
                .pluck('id')
                .value() as number[];
        },

        getStudentIdsByDolIdString: (state: any) => (searchString: string): number[] => {
            return _.chain(state.students || [])
                .filter((s: StudentProfile) => {
                    return null === searchString || (undefined !== s.dolId && new RegExp(searchString, 'i').test(s.dolId));
                })
                .pluck('id')
                .value();
        },

        getStudentNameIndexById: (state) => (id: number): number => {

            return state.students.findIndex((user: StudentProfile) => user.id === Number(id));
        }
    },

    mutations: {
        setStudents(state, students: StudentProfile[]) {
            state.students = _.sortBy(students, (s: StudentProfile) => {
                return s.getFullName();
            });
        },

        setStudent(state, student: StudentProfile) {
            const s = _.find(state.students, (st: StudentProfile) => {
                return student.id === st.id;
            });

            if (s) {
                s.dolId = student.dolId;
                s.firstName = student.firstName;
                s.middleName = student.middleName;
                s.lastName = student.lastName;
                s.suffix = student.suffix;
                s.certificationDate = student.certificationDate;
                s.jobCorpsCenterId = student.jobCorpsCenterId;
                s.jobCorpsCenter = s.getJobCorpsCenter();
                s.active = student.active;
                s.studentStatusDate = student.studentStatusDate;
                s.certificates = _.map(student.certificates, (n: number) => n);
                s.formattedName = s.getFullName();
            } else {
                state.students.push(student);
            }
        }
    },

    actions: {

        saveStudent(state, student: StudentProfile) {

            const data = JSON.stringify(student);

            return new Promise((resolve, reject) => {
                api().post('/student', data)
                    .then((response) => {
                        state.commit('setStudent', response.data as IStudentProfile);
                        resolve();
                    })
                    .catch((err) => {
                        reject(err);
                    });
            });
        },

        loadStudents({state, commit, dispatch}) {

            dispatch('loadCertificates', true, {root: true})
                .then(() => {
                    api().get('/students')
                        .then((response) => {

                            const students = _.chain(response.data as StudentProfile[])
                                .map((s: IStudentProfile) => {
                                    return new StudentProfile(s);
                                })
                                .sortBy((s: StudentProfile) => {
                                    return s.firstName;
                                })
                                .sortBy((s: StudentProfile) => {
                                    return s.lastName;
                                })
                                .value();

                            commit('setStudents', students);
                        })
                        .catch((err) => {
                            // TODO: Do something with error
                        });
                    });
        }
    }
};
